<!-- Panel contents for bulk tag add/remove. Parent owns container element and styling of the container. -->
<template>
  <h4 class="subtitle mb-3 is-size-6 is-small-mobile">{{ titleText }}</h4>

  <div class="field is-fullwidth mb-3">
    <label class="label is-small-mobile">Add:</label>
    <div class="control is-fullwidth">
      <TagInput
        class="is-fullwidth is-block is-small-mobile"
        tag-list-class="mb-2"
        :model-value="tagsToAdd"
        :all-tags="allTagsForAdd"
        :allow-new="true"
        placeholder="Search or create tags..."
        @update:model-value="(val) => (tagsToAdd = val)"
        @create="handleCreateTag"
      />
    </div>
  </div>

  <div class="field is-fullwidth mb-4">
    <label class="label is-small-mobile">Remove:</label>
    <div class="control is-fullwidth">
      <TagInput
        class="is-fullwidth is-block is-small-mobile"
        tag-list-class="mb-2"
        :model-value="tagsToRemove"
        :all-tags="allTagsForRemove"
        :allow-new="false"
        placeholder="Search existing tags..."
        @update:model-value="(val) => (tagsToRemove = val)"
      />
    </div>
  </div>

  <div class="field is-grouped is-justify-content-space-between">
    <div class="control">
      <button class="button is-small-mobile" @click="cancel">Cancel</button>
    </div>
    <div class="control">
      <button
        class="button is-small-mobile"
        :class="{
          'is-loading': isBusy,
          'is-outlined': !canApplyChanges,
          'is-success': canApplyChanges,
        }"
        :disabled="!canApplyChanges"
        @click="applyChanges"
      >
        <span>Apply changes</span>
      </button>
    </div>
  </div>
</template>

<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import { Flash } from "~/jose_modern/flash";

  import TagInput from "./tag-input.vue";

  import { errorHelpTip } from "./lib/constants";

  const flashError = (msg) => Flash.error(msg);
  const flashSuccess = (msg) => Flash.success(msg);
  const flashWarning = (msg) => Flash.warning(msg);

  const props = defineProps({
    // The model ids the bulk action will apply to.
    ids: { type: Array, required: true },
    // "recipe" | "ingredient", used in the title only.
    entityLabel: { type: String, required: true },
    // Tag store. Must expose: allTags, createTag({modelId, tag}),
    // bulkAddTags({ids, tagNames}), bulkRemoveTags({ids, tagNames}), refreshCounts().
    store: { type: Object, required: true },
  });

  const emit = defineEmits(["close", "done"]);

  let tagsToAdd = _ref([]);
  let tagsToRemove = _ref([]);
  let isBusy = _ref(false);

  const titleText = _computed(() => `Add/remove tags to ${props.entityLabel}s`);

  const allTagsForAdd = _computed(() => {
    return props.store.allTags.filter(
      (tag) => !tagsToRemove.value.some((removed) => removed.name === tag.name)
    );
  });

  const allTagsForRemove = _computed(() => {
    return props.store.allTags.filter((tag) => !tagsToAdd.value.some((added) => added.name === tag.name));
  });

  const canApplyChanges = _computed(() => {
    return tagsToAdd.value.length > 0 || tagsToRemove.value.length > 0;
  });

  function cancel() {
    emit("close");
  }

  async function handleCreateTag(newTagName) {
    newTagName = newTagName.trim();
    if (!newTagName) return;

    if (tagsToAdd.value.some((tag) => tag.name.toLowerCase() === newTagName.toLowerCase())) {
      return;
    }

    const existing = props.store.allTags.find(
      (t) => t.name.toLowerCase() === newTagName.toLowerCase()
    );
    if (existing) {
      tagsToAdd.value = [...tagsToAdd.value, existing];
      return;
    }

    const createdTag = await props.store.createTag({
      modelId: 0,
      tag: { name: newTagName },
    });
    tagsToAdd.value = [...tagsToAdd.value, createdTag];
  }

  async function applyChanges() {
    isBusy.value = true;

    const addNames = tagsToAdd.value.map((t) => t.name.trim()).filter((n) => n.length > 0);
    const removeNames = tagsToRemove.value.map((t) => t.name.trim()).filter((n) => n.length > 0);

    try {
      const promises = [];
      if (addNames.length > 0) {
        promises.push(props.store.bulkAddTags({ ids: props.ids, tagNames: addNames }));
      }
      if (removeNames.length > 0) {
        promises.push(props.store.bulkRemoveTags({ ids: props.ids, tagNames: removeNames }));
      }
      const results = await Promise.all(promises);
      const skippedLockedCount = Math.max(0, ...results.map((count) => count ?? 0));
      if (skippedLockedCount > 0) {
        const noun = `locked ${props.entityLabel}${skippedLockedCount === 1 ? "" : "s"}`;
        flashWarning(
          `${skippedLockedCount} ${noun} ${skippedLockedCount === 1 ? "was" : "were"} skipped.`
        );
      } else {
        flashSuccess("Updated tags.");
      }
      props.store.refreshCounts().catch(console.error);
    } catch (err) {
      console.error(err);
      flashError(`Failed to update tags. ${errorHelpTip}`);
    }
    isBusy.value = false;
    emit("done");
  }
</script>
